import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Card, Avatar, Tooltip, Badge, Menu, Dropdown, Button } from 'antd';
import { UserOutlined, SettingOutlined, LogoutOutlined, HomeOutlined, UserAddOutlined, ShopOutlined, TagOutlined, EnvironmentOutlined, ExclamationCircleOutlined, CrownOutlined, MenuOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { isAuthenticated } from 'services/auth';
import { Drawer, IconButton, Typography, Box } from '@mui/material';

const UnifiedDashboard = ({ children }) => {
  const { user } = isAuthenticated();
  const history = useHistory();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detectar se é mobile
  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // No mobile, manter drawer fechado por padrão
      if (mobile) {
        setDrawerVisible(false);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    console.log('Fechando drawer MUI...');
    setDrawerVisible(false);
    console.log('Drawer MUI fechado');
  }, []);

  // Menu items baseado na role do usuário
  const getMenuItems = () => {
    if (!user) return [];

    const createProductBtn = {
      key: 'create-product',
      icon: <ShopOutlined />,
      label: <span>Criar Produto</span>,
      path: `/add-product/${user._id}`,
      highlight: true
    };
    const commonItems = [
      {
        key: 'dashboard',
        icon: <HomeOutlined />,
        label: 'Dashboard',
        path: '/user/dashboard'
      },
      createProductBtn
    ];

    if (user.role === 'admin') {
      return [
        ...commonItems,
        {
          type: 'divider'
        },
        {
          key: 'admin-section',
          type: 'group',
          label: 'Administração',
          children: [
            {
              key: 'users',
              icon: <UserAddOutlined />,
              label: 'Gerenciar Usuários',
              path: '/admin/users'
            },
            {
              key: 'categories',
              icon: <TagOutlined />,
              label: 'Categorias',
              path: '/admin/add-category'
            },
            {
              key: 'locations',
              icon: <EnvironmentOutlined />,
              label: 'Localizações',
              path: '/admin/add-location'
            },
            {
              key: 'reports',
              icon: <ExclamationCircleOutlined />,
              label: 'Produtos Reportados',
              path: '/admin/reported-products'
            },
            {
              key: 'sponsored',
              icon: <CrownOutlined />,
              label: 'Anúncios Patrocinados',
              path: '/admin/sponsored-ads'
            }
          ]
        },
      ];
    } else {
      return commonItems;
    }
  };

  const handleMenuClick = (item) => {
    console.log(item.path)
    if (item.path) {
      history.push(item.path);
      // Fecha o drawer após clicar em um item
      if (isMobile) {
        handleCloseDrawer();
      }
    }
  };

  const renderMenuItem = (item) => {
    if (item.type === 'divider') {
      return <div key={item.key} className="menu-divider" />;
    }
    
    if (item.type === 'group') {
      return (
        <div key={item.key} className="menu-group">
          <div className="menu-group-label">
            {item.label}
          </div>
          {item.children.map(child => renderMenuItem(child))}
        </div>
      );
    }

    const isActive = history.location.pathname === item.path;
    const isHighlight = item.highlight;
    return (
      <div
        key={item.key}
        onClick={() => handleMenuClick(item)}
        className={`nav-menu-item ${isActive ? 'active' : ''} ${isHighlight ? 'highlight' : ''}`}
      >
        <span className={`nav-menu-icon ${isActive ? 'active' : isHighlight ? 'highlight' : 'default'}`}>
          {item.icon}
        </span>
        <span>{item.label}</span>
      </div>
    );
  };

  return (
    <div className="unified-dashboard-outer">
      <Drawer
        anchor="left"
        open={drawerVisible}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? 280 : 250,
            maxHeight: '100vh',
            backgroundColor: '#fff',
            borderRight: '2px solid rgba(4, 34, 184, 0.1)',
            boxShadow: '-2px 0 12px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '2px solid rgba(4, 34, 184, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            Menu
          </Typography>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              background: 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              width: 32,
              height: 32,
              color: '#64748b',
              '&:hover': {
                background: '#f1f5f9',
                borderColor: '#0422b8',
                color: '#0422b8',
                transform: 'scale(1.05)'
              },
              '&:active': {
                transform: 'scale(0.95)'
              }
            }}
          >
            <ArrowLeftOutlined />
          </IconButton>
        </Box>
        <Box sx={{ p: 1 }}>
          <nav className="drawer-nav">
            {getMenuItems().map(item => renderMenuItem(item))}
          </nav>
        </Box>
      </Drawer>
      
      {/* Sidebar fixa no desktop */}
      {!isMobile && (
        <aside className="sidebar-fixed">
          <nav className="sidebar-nav">
            {getMenuItems().map(item => renderMenuItem(item))}
          </nav>
        </aside>
      )}

      <main
        className={`unified-main-content ${isMobile ? 'mobile' : 'desktop'}`}
      >
        {/* Header */}
        {isMobile ? 
        (<header className="unified-header">
          <div className="unified-header-info">
            <Button
              icon={<MenuOutlined />}
              className={`mobile-menu-btn ${isMobile ? 'mobile' : 'desktop'}`}
              onClick={() => setDrawerVisible(true)}
            />
            {user.role === 'admin' ? 'Painel de Administração' : 'Painel do Usuário'}
          </div>
        </header>) : <></>
      }
        
        {/* Content Area */}
        <section className="unified-content-section">
          <div className="unified-content-wrapper">{children}</div>
        </section>
      </main>
    </div>
  );
};

export default UnifiedDashboard;
